import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Panel, ListGroup, ListGroupItem, Button, ButtonGroup } from 'react-bootstrap';

const DISTANCE_OPTIONS = [null, 50, 250, 500, 1000, 2500, 5000];

const FilterByDistance = ({
	projectFilter,
	setProximityDistance,
	setMapPinMyLocation,
	mapPinMyLocation,
	mapPinPickMode,
	setMapPinPickMode,
	setIsFlyoutRightActive
}) => {
	const [distanceFromMe, setDistanceFromMe] = useState(null);
	const [originMode, setOriginMode] = useState('location'); // 'location' | 'map'

	const clearDistance = () => {
		setDistanceFromMe(null);
		setMapPinPickMode(false);
		setProximityDistance(null, () => {
			setMapPinMyLocation(null);
		});
	};

	const applyMyLocation = (feet) => {
		navigator.geolocation.getCurrentPosition(p => {
			setDistanceFromMe(feet);
			setMapPinPickMode(false);
			setProximityDistance(feet, () => {
				setMapPinMyLocation(p.coords.latitude, p.coords.longitude);
			});
		}, err => {
			alert('Could not access location: ' + err.message);
		});
	};

	const applyMapOrigin = (feet) => {
		setDistanceFromMe(feet);
		setProximityDistance(feet, () => {
			if (mapPinMyLocation) {
				// Keep existing pin; proximity effect recalculates with new radius.
				setMapPinPickMode(false);
				return;
			}
			setMapPinPickMode(true);
			if (typeof setIsFlyoutRightActive === 'function') {
				setIsFlyoutRightActive(false);
			}
		});
	};

	const selectOriginMode = (mode) => {
		setOriginMode(mode);
		if (distanceFromMe == null) {
			setMapPinPickMode(false);
			return;
		}
		if (mode === 'location') {
			applyMyLocation(distanceFromMe);
		} else {
			applyMapOrigin(distanceFromMe);
		}
	};

	const selectDistance = (feet) => {
		if (feet === null) {
			clearDistance();
			return;
		}
		if (originMode === 'location') {
			applyMyLocation(feet);
		} else {
			applyMapOrigin(feet);
		}
	};

	const distanceItems = DISTANCE_OPTIONS.map(item => (
		<ListGroupItem
			key={String(item)}
			onClick={() => selectDistance(item)}>
			<input
				className="distanceRadio"
				type="radio"
				name="distanceFromMe"
				checked={distanceFromMe === item}
				readOnly />
			{item === null ? 'None' : `${item} feet`}
		</ListGroupItem>
	));

	return (
		<Panel
			className={classnames({
				activeFilter: distanceFromMe != null || mapPinPickMode || mapPinMyLocation
			})}>
			<Panel.Heading>
				<Panel.Title toggle>Distance</Panel.Title>
			</Panel.Heading>
			<Panel.Collapse>
				<Panel.Body>
					<div className="distanceOriginMode">
						<div className="distanceOriginMode-label">Origin</div>
						<ButtonGroup vertical className="w-100">
							<Button
								bsSize="small"
								bsStyle={originMode === 'location' ? 'primary' : 'default'}
								onClick={() => selectOriginMode('location')}>
								My location
							</Button>
							<Button
								bsSize="small"
								bsStyle={originMode === 'map' ? 'primary' : 'default'}
								onClick={() => selectOriginMode('map')}>
								Pick on map
							</Button>
						</ButtonGroup>
						{mapPinPickMode
							? (
								<div className="distancePickHint">
									Click the map to set the distance origin.
								</div>
							)
							: null}
						{originMode === 'map' && mapPinMyLocation && !mapPinPickMode
							? (
								<div className="distancePickHint">
									Pin set. Choose a radius, or pick again to move it.
									{' '}
									<button
										type="button"
										className="distanceRepickLink"
										onClick={() => {
											setMapPinPickMode(true);
											if (typeof setIsFlyoutRightActive === 'function') {
												setIsFlyoutRightActive(false);
											}
										}}>
										Repick
									</button>
								</div>
							)
							: null}
					</div>
					<div className="distanceOriginMode-label">Radius</div>
					<ListGroup>
						{distanceItems}
					</ListGroup>
				</Panel.Body>
			</Panel.Collapse>
		</Panel>
	);
};

export default FilterByDistance;

FilterByDistance.propTypes = {
	projectFilter: PropTypes.object,
	setProximityDistance: PropTypes.func,
	setMapPinMyLocation: PropTypes.func,
	mapPinMyLocation: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number
	}),
	mapPinPickMode: PropTypes.bool,
	setMapPinPickMode: PropTypes.func,
	setIsFlyoutRightActive: PropTypes.func
};
